import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

const Show = ({ open, onClose, itemData }) => {
  if (!itemData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">
          Détails du produit
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Nom
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.name}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.description}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Prix
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.price?.toLocaleString('fr-FR')} FCFA
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Type de produit
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.is_goodie ? 'Goodie' : 'Produit standard'}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Ordre d'affichage
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.show_order || 0}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Événement
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.event?.name || '-'}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Catégorie
          </Typography>
          <Typography variant="body1" gutterBottom>
            {itemData.category?.name || '-'}
          </Typography>
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Points de vente
          </Typography>
          {itemData.productSalePoints?.map((point) => (
            <Typography key={point.id} variant="body1" gutterBottom>
              {point.salePoint.name} ({point.quantity})
            </Typography>
          ))}
        </Box>

        {itemData.is_goodie && itemData.associatedTickets && itemData.associatedTickets.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tickets associés
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {itemData.associatedTickets.map((ticket) => (
                <Chip
                  key={ticket.id}
                  label={ticket.name}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ 
            backgroundColor: 'primary.main',
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

Show.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  itemData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.number,
    event: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    category: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    productSalePoints: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      quantity: PropTypes.number,
      salePoint: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    })),
    tax: PropTypes.string,
    is_goodie: PropTypes.bool,
    show_order: PropTypes.number,
    associatedTickets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      ticket_type: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      }),
      price: PropTypes.number,
      quantity: PropTypes.number,
      image: PropTypes.string,
      show_order: PropTypes.number
    }))
  }),
};

export default Show;